import { apiClientWithoutAccount } from '@/api/client'
import { extractData, extractArray, extractWrapped } from '@/api/responseExtractors'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'

const BASE_PATH = '/manager/accounts'

/**
 * Conta de manager disponível
 */
export interface ManagerAccount {
  id: string
  name: string
  cnpj?: string | null
}

/**
 * Resposta da listagem de contas
 * GET /manager/accounts
 */
export interface AccountsListResponse {
  data: {
    accounts: ManagerAccount[]
  }
  meta: Record<string, unknown>
}

/**
 * Resposta da seleção de conta
 * POST /manager/accounts/select
 */
export interface AccountSelectResponse {
  data: {
    current_account_id: string
  }
}

/**
 * Serviço para gerenciar contas de manager.
 * Usa client sem X-ACCOUNT-ID (apiClientWithoutAccount).
 */
export function useAccountsApi() {
  const authStore = useAuthStore()

  const listAccounts = async (): Promise<ManagerAccount[]> => {
    const res = await apiClientWithoutAccount.get(BASE_PATH, undefined)
    const data = extractData(res)
    const inner = (data as { data?: { accounts?: ManagerAccount[] } })?.data ?? data
    return extractArray<ManagerAccount>(inner, ['accounts'])
  }

  const selectAccount = async (accountId: string): Promise<string> => {
    const res = await apiClientWithoutAccount.post(`${BASE_PATH}/select`, { id: accountId })
    const data = extractData(res)
    const inner = data as { data?: { current_account_id?: string; business_cnpj?: string } }
    const currentAccountId = inner?.data?.current_account_id ?? extractWrapped<string>(data, 'current_account_id')
    const businessCnpj = inner?.data?.business_cnpj ?? null
    if (currentAccountId) {
      authStore.setAccountId(currentAccountId)
      authStore.setBusinessCnpj(businessCnpj)
      useUserStore().invalidateManagerMe()
      return currentAccountId
    }
    throw new Error('No account ID returned from select')
  }

  return {
    listAccounts,
    selectAccount,
  }
}
